import ky from 'ky'

import * as qs from 'qs'
import { getIsAuth } from 'storage/user-storage'

import { generateCodeChallenge, generateCodeVerifier } from '../utilities/encoding'
import config from '../config/api'

const cognitoInstance = ky.extend({
  prefixUrl: config.cognito.provider,
})

const fetchToken = (data) => {
  return cognitoInstance
    .post('oauth2/token', {
      body: qs.stringify(data),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
    .catch(e => console.log(e))
}

export const redirectToCognitoPage = (action = 'login', skipRedirect = false) => {
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = generateCodeChallenge(codeVerifier)

  sessionStorage.clear()
  localStorage.removeItem('token')
  localStorage.removeItem('refresh')
  localStorage.removeItem('lastAuth')
  localStorage.removeItem('expiresIn')
  localStorage.removeItem('username')
  localStorage.setItem('authenticated', 'false')
  localStorage.setItem('codeVerifier', codeVerifier)
  localStorage.setItem('codeChallenge', codeChallenge)

  const { provider, clientId, redirectUri } = config.cognito
  const host = `${provider}/${action}`
  const params = qs.stringify({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  })
  const authURL = `${host}?${params}`

  if (!skipRedirect) {
    window.location.replace(authURL)
  }

  return authURL
}

export const Logout = () => {
  console.log('logout')
  redirectToCognitoPage('login')

  return null
}

export async function awsCallTokenEndpoint(grantType, accessToken, codeVerifier) {
  // NOTE ON client_secret. This is a browser SPA, so REACT_APP_CLIENT_SECRET is compiled
  // into the public bundle and is readable by anyone who opens devtools — it is not a
  // secret in any meaningful sense.
  //
  // It is also unnecessary: this flow already sends `code_verifier`, i.e. PKCE, which is
  // exactly the mechanism that replaces a client secret for public clients. The fix is to
  // switch the Cognito app client to a PUBLIC client (no secret) and drop the build arg.
  // That is an AWS-side change, so it is NOT done here — removing the value unilaterally
  // would break login while the app client still demands it.
  //
  // The request is written to omit the field when unset, so once Cognito is switched the
  // only change needed is deleting the build arg. No code change on the day.
  const { clientId, clientSecret, redirectUri } = config.cognito
  const data = {
    grant_type: grantType,
    client_id: clientId,
    ...(clientSecret ? { client_secret: clientSecret } : {}),
    code: accessToken,
    scope: 'funbot/arbitrage.ui',
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  }

  const awsResponse = await fetchToken(data)

  if (awsResponse.status === 200) {
    return awsResponse.json()
  }
}

export async function awsCallTokenRefresh(grantType, refreshToken) {
  const data = {
    grant_type: grantType,
    client_id: config.cognito.clientId,
    refresh_token: refreshToken,
  }

  const awsResponse = await fetchToken(data)

  if (awsResponse.status === 200) {
    return awsResponse.json()
  }
}

const getUserInfo = (token) => {
  return ky
    .post(
      'https://cognito-idp.eu-west-1.amazonaws.com/',
      {
        json: { AccessToken: token },
        headers: {
          'X-Amz-target': 'AWSCognitoIdentityProviderService.GetUser',
          'Content-Type': 'application/x-amz-json-1.1',
        },
      }
    )
    .json()
}

export const getAwsToken = async (requestCallback) => {
  const data = await requestCallback()
  const userInfo = await getUserInfo(data?.access_token)

  if (!data || !userInfo) {
    return awsLogout()
  }

  const username =
    userInfo?.UserAttributes?.find((attr) => attr.Name === 'name')?.Value ||
    userInfo?.UserAttributes?.find((attr) => attr.Name === 'email')?.Value

  localStorage.setItem('token', data.access_token)
  if (data && data.refresh_token) localStorage.setItem('refresh', data.refresh_token)
  localStorage.setItem('authenticated', 'true')
  localStorage.setItem('lastAuth', JSON.stringify(Number(new Date())))
  localStorage.setItem('expiresIn', JSON.stringify(data.expires_in * 1000))
  localStorage.setItem('username', username)
}

export const awsLogout = () => {
  redirectToCognitoPage('logout')
}

export const isAuthExpired = () => {
  const isAuth = getIsAuth()

  if (!isAuth) {
    return false
  }

  const lastAuth = localStorage.getItem('lastAuth')
  const expiresIn = localStorage.getItem('expiresIn')

  return new Date() - lastAuth > expiresIn - 300000
}
